import { Alert, Divider, Empty, Modal, Select, Space, Spin, Tag, Typography } from "antd";
import { SafetyCertificateOutlined, SolutionOutlined, UserOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Text } = Typography;

const EXPERTS_ENDPOINT = "/api/expertise/expert/all";
const ASSIGN_ENDPOINT = (codeId, victimeId) => `/api/sinistre/expert/codeId/${codeId}/${victimeId}`;

const typeLabelFromExpert = (expert) =>
  expert?.typeExpert?.libelle ||
  expert?.typeExpert?.label ||
  expert?.type ||
  expert?.profession ||
  expert?.categorie?.libelle ||
  expert?.categorie ||
  expert?.specialite ||
  "Autre";

const nameLabelFromExpert = (expert) => {
  const nom = expert?.nom || "";
  const prenom = expert?.prenom || "";
  const raison = expert?.raisonSociale || "";
  const computed = `${nom}${prenom ? ` ${prenom}` : ""}`.trim();
  return computed || raison || expert?.displayName || "Expert";
};

const sansAccents = (s) =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toUpperCase();

// Clé d'IDENTITÉ d'un expert. IMPORTANT : le backend (SinistreService.addExpert →
// PersonneService.addPersonne) recrée une nouvelle Personne avec un codeId FRAIS à chaque
// affectation. Le « même » expert existe donc sous plusieurs codeId. On déduplique donc par
// NOM COMPLET (nom + prénom / raison sociale), jamais par codeId — sinon on ne peut ni
// reconnaître un expert déjà affecté, ni empêcher les doublons d'affichage du catalogue.
const expertKey = (expert) => sansAccents(nameLabelFromExpert(expert));

const victimeLabel = (victime) => {
  const p = victime?.personne;
  const nom = `${p?.nom || ""} ${p?.prenom || ""}`.trim();
  return nom || victime?.nom || "Victime";
};

/**
 * Affectation d'expert(s) À UNE VICTIME.
 *
 * Refonte : l'affectation est désormais entièrement centrée sur la VICTIME (et non sur le
 * dossier). On choisit d'abord la victime, on voit les experts déjà affectés à CETTE victime,
 * puis on ajoute des experts. La déduplication se fait par (victime × identité d'expert), ce
 * qui permet d'affecter le MÊME expert à plusieurs victimes du même dossier — le bug corrigé.
 */
const AssignExpertsModal = ({ open, sinistre, expertises, victime, onClose, onSuccess }) => {
  const [availableExperts, setAvailableExperts] = useState([]);
  // Expertises du dossier — récupérées par le modal lui-même (le prop `expertises` sert de
  // seed anti-flicker) pour que TOUS les appelants (V1/V2) aient les mêmes données à jour.
  const [dossierExpertises, setDossierExpertises] = useState(expertises || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [selectedVictime, setSelectedVictime] = useState("");

  const sinistreCodeId = sinistre?.codeId;

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setTypeFilter(null);
      setDossierExpertises(expertises || []);
      // Ouverture pré-ciblée sur une victime (depuis sa carte) ou choix manuel.
      setSelectedVictime(victime?.codeId || "");
      fetchExperts();
      fetchExpertises();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, victime]);

  const fetchExperts = () => {
    setLoading(true);
    axiosInstance
      .get(EXPERTS_ENDPOINT)
      .then((res) => setAvailableExperts(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des experts");
      })
      .finally(() => setLoading(false));
  };

  const fetchExpertises = () => {
    if (!sinistre?.codeId) return;
    axiosInstance
      .get(`/api/expertise/sinistre/${sinistre.codeId}`)
      .then((res) => setDossierExpertises(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error(err));
  };

  // Experts DÉJÀ affectés à la victime sélectionnée (déduits des expertises du dossier).
  const expertsDeLaVictime = useMemo(() => {
    if (!selectedVictime) return [];
    return (dossierExpertises || [])
      .filter((e) => {
        const vId = e?.beneficiaire?.codeId || e?.victime?.codeId;
        return vId === selectedVictime && e?.expert;
      })
      .map((e) => e.expert);
  }, [dossierExpertises, selectedVictime]);

  // Ensemble des identités d'experts déjà affectés à cette victime (dédup par nom).
  const assignedKeys = useMemo(
    () => new Set(expertsDeLaVictime.map(expertKey)),
    [expertsDeLaVictime]
  );

  const victimeOptions = useMemo(
    () =>
      (sinistre?.victimes || []).map((v) => ({
        value: v.codeId,
        label: victimeLabel(v),
        _adversaire: !!v?.adversaire,
      })),
    [sinistre]
  );

  const typeOptions = useMemo(() => {
    const unique = new Set(availableExperts.map(typeLabelFromExpert).filter(Boolean));
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }))
      .map((label) => ({ value: label, label }));
  }, [availableExperts]);

  // Catalogue proposable : filtré par type (optionnel), en EXCLUANT les experts déjà affectés
  // à la victime, et DÉDUPLIQUÉ par identité (le catalogue contient des copies minted).
  const options = useMemo(() => {
    const base = typeFilter
      ? availableExperts.filter((e) => typeLabelFromExpert(e) === typeFilter)
      : availableExperts;
    const byKey = new Map();
    base.forEach((e) => {
      const k = expertKey(e);
      if (assignedKeys.has(k) || byKey.has(k)) return; // déjà affecté à la victime ou doublon
      byKey.set(k, {
        value: e.codeId,
        label: nameLabelFromExpert(e),
        _type: typeLabelFromExpert(e),
      });
    });
    return Array.from(byKey.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" })
    );
  }, [availableExperts, typeFilter, assignedKeys]);

  const handleAssign = async () => {
    if (!sinistreCodeId) {
      toast.error("Identifiant du sinistre introuvable");
      return;
    }
    if (!selectedVictime) {
      toast.warning("Veuillez d'abord sélectionner la victime à expertiser.");
      return;
    }

    const selectedExperts = availableExperts.filter((e) => selectedIds.includes(e.codeId));
    if (selectedExperts.length === 0) {
      toast.warning("Veuillez sélectionner au moins un expert à affecter.");
      return;
    }

    // Dédup PAR VICTIME et par IDENTITÉ (pas par codeId) : on n'affecte pas deux fois le même
    // expert à la même victime, mais on autorise le même expert sur une AUTRE victime.
    const seen = new Set();
    const toAdd = selectedExperts.filter((e) => {
      const k = expertKey(e);
      if (assignedKeys.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    if (toAdd.length === 0) {
      toast.info("Cet expert est déjà affecté à cette victime.");
      return;
    }

    setSaving(true);
    try {
      // Séquentiel : l'affectation backend recrée une Personne + une Expertise ; on évite les
      // courses concurrentes sur la même victime.
      for (const expert of toAdd) {
        // eslint-disable-next-line no-await-in-loop
        await axiosInstance.post(ASSIGN_ENDPOINT(sinistreCodeId, selectedVictime), expert);
      }
      toast.success(
        toAdd.length > 1
          ? `${toAdd.length} experts affectés à la victime avec succès`
          : "Expert affecté à la victime avec succès"
      );
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'affectation des experts");
    } finally {
      setSaving(false);
    }
  };

  const tagRender = (props) => {
    const { label, value, onClose: onTagClose } = props;
    const option = options.find((item) => item.value === value);
    const type = option?._type;
    return (
      <Tag closable onClose={onTagClose} style={{ marginRight: 4 }} color="blue">
        {label}
        {type ? <span style={{ opacity: 0.7 }}> — {type}</span> : null}
      </Tag>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <SafetyCertificateOutlined style={{ color: "#764ba2" }} />
          Affecter un expert à une victime
        </Space>
      }
      centered
      open={open}
      onCancel={onClose}
      onOk={handleAssign}
      okText="Affecter"
      cancelText="Fermer"
      okButtonProps={{ disabled: !selectedVictime || selectedIds.length === 0 }}
      confirmLoading={saving}
      destroyOnClose
      width={560}
    >
      {loading ? (
        <div className="text-center" style={{ padding: "24px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 1. VICTIME (obligatoire) */}
          <div style={{ marginBottom: 4 }}>
            <Text strong>
              <UserOutlined style={{ marginRight: 6 }} />
              Victime à expertiser <span style={{ color: "#ff4d4f" }}>*</span>
            </Text>
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder="Sélectionnez la victime"
            options={victimeOptions}
            value={selectedVictime || undefined}
            onChange={(v) => {
              setSelectedVictime(v);
              setSelectedIds([]); // repartir d'une sélection propre par victime
            }}
            optionFilterProp="label"
            showSearch
            optionRender={(opt) => (
              <Space>
                {opt.data.label}
                {opt.data._adversaire ? <Tag color="orange">Adversaire</Tag> : null}
              </Space>
            )}
          />

          {!selectedVictime ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Commencez par choisir la victime"
              description="Chaque expert est affecté à une victime précise. Un même expert peut être affecté à plusieurs victimes du dossier."
            />
          ) : (
            <>
              {/* Experts déjà affectés à CETTE victime */}
              <Divider style={{ margin: "16px 0 10px" }} orientation="left" plain>
                <SolutionOutlined style={{ marginRight: 6 }} />
                Experts déjà affectés à cette victime
              </Divider>
              {expertsDeLaVictime.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aucun expert affecté à cette victime"
                  style={{ margin: "4px 0 8px" }}
                />
              ) : (
                <div style={{ marginBottom: 8 }}>
                  <Space size={[4, 8]} wrap>
                    {expertsDeLaVictime.map((e, i) => (
                      <Tag key={`${e?.codeId || i}`} color="green" style={{ padding: "2px 8px" }}>
                        {nameLabelFromExpert(e)}
                        <span style={{ opacity: 0.7 }}> — {typeLabelFromExpert(e)}</span>
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* 2. Ajout d'experts */}
              <Divider style={{ margin: "12px 0 10px" }} orientation="left" plain>
                Ajouter des experts
              </Divider>

              {availableExperts.length === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  message="Aucun expert disponible"
                  description="Veuillez d'abord créer des experts depuis les paramètres de sinistre."
                />
              ) : (
                <>
                  <Select
                    allowClear
                    style={{ width: "100%", marginBottom: 10 }}
                    placeholder="Filtrer par type (optionnel) — Médical, Automobile, etc."
                    options={typeOptions}
                    value={typeFilter || undefined}
                    onChange={(value) => setTypeFilter(value ?? null)}
                    showSearch
                    optionFilterProp="label"
                  />
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    options={options}
                    value={selectedIds}
                    onChange={setSelectedIds}
                    placeholder="Choisir un ou plusieurs experts à affecter à cette victime"
                    optionFilterProp="label"
                    showSearch
                    tagRender={tagRender}
                    notFoundContent={
                      typeFilter
                        ? "Aucun expert de ce type (ou déjà tous affectés à la victime)"
                        : "Aucun expert disponible"
                    }
                  />
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    Les experts déjà affectés à cette victime n'apparaissent pas dans la liste.
                  </Text>
                </>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

AssignExpertsModal.propTypes = {
  open: PropTypes.bool,
  sinistre: PropTypes.object,
  /** Toutes les expertises du dossier (pour déduire les experts par victime). */
  expertises: PropTypes.array,
  /** Optionnel : ouvre le modal pré-ciblé sur une victime. */
  victime: PropTypes.object,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

AssignExpertsModal.defaultProps = {
  expertises: [],
};

export default AssignExpertsModal;
